import { uniq } from 'lodash';
import { IHistory, IUndoFunction, LayerData, User, Rect, HistoryBuffer, Drawing, HistoryStats } from './interfaces';
import { getLayerSafe, getLayer } from './drawing';
import { historyBufferStats, pushHistoryBufferEntry, removeHistoryBufferEntry } from './historyBuffer';
import { UNDO_LIMIT } from './constants';
import { setLayerState, toLayerState, layerFromState, redrawLayerThumb, layerChanged, isTextLayer } from './layer';
import { cloneRect, makeIntegerRect, rectsIntersection, rectToString } from './rect';
import type { Editor } from '../services/editor';
import { redraw, redrawDrawing } from '../services/editorUtils';
import { setPoint } from './point';
import { setSurfaceState, getSurfaceState } from './toolSurface';
import { serializeMask, deserializeMask } from './mask';
import { logAction } from './actionLog';
import { addLayerToDrawing, removeLayerFromDrawing, sendLayerOrder } from './layerToolHelpers';
import { findLastIndex } from './utils';
import { selectLayer } from '../services/layerActions';

let stateId = 0;

const LOG_ON = false;
const LOG = false;

function log(...args: any[]) {
  LOG_ON && DEVELOPMENT && !TESTS && console.log(...args);
}

function isValidDirtyRect(r: Rect, { rect }: Drawing) {
  return r.x === (r.x | 0) && r.x === (r.x | 0) && r.x === (r.x | 0) && r.x === (r.x | 0) &&
    rect.x <= r.x && (rect.x + rect.w) >= (r.x + r.w) && rect.y <= r.y && (rect.y + rect.h) >= (r.y + r.h);
}

export class History implements IHistory {
  undos: IUndoFunction[][] = [];
  redos: IUndoFunction[][] = [];
  preundos: IUndoFunction[] = [];
  private undosBuffer: HistoryBuffer = { sheets: [] };
  private redosBuffer: HistoryBuffer = { sheets: [] };
  private transaction = false;
  private discardedUndos = false;
  constructor(private user: User, private editor: Editor) {
  }
  stats(stats: HistoryStats) {
    historyBufferStats(this.undosBuffer, stats);
    historyBufferStats(this.redosBuffer, stats);
  }
  hadDiscardedUndos() {
    return this.redos.length === UNDO_LIMIT && this.discardedUndos;
  }
  private log(_message: string) {
    // this.editor.log(`[history] ${this.transaction ? '  ' : ''}${message}`);
  }
  private undoOrRedo(from: IUndoFunction[][], to: IUndoFunction[][]) {
    if (this.transaction) throw new Error('transaction is not ended');

    if (this.preundos.length) {
      logAction(`Preundos not commited [${undoLog(this.preundos)}]`);
      throw new Error('Preundos not commited');
    }

    const fromTransaction = from.pop();
    const toTransaction: IUndoFunction[] = [];

    if (!fromTransaction) return;

    while (fromTransaction.length) {
      const item = fromTransaction.pop()!;
      toTransaction.push(item());

      if (item.lastX !== undefined && item.lastY !== undefined) {
        setPoint(this.editor.lastPoint, item.lastX, item.lastY);
      }
    }

    to.push(toTransaction);
  }
  attachLastPoint(x: number, y: number) {
    if (this.undos.length) {
      const undo = this.undos[this.undos.length - 1][0];

      if (undo) {
        undo.lastX = x;
        undo.lastY = y;
      } else {
        console.error(`Empty undo list`);
      }
    }
  }
  isLastEntryMove(layerId: number) {
    const last = this.undos[this.undos.length - 1];
    return !!last?.some(u => u.layerId === layerId && !!u.isMove);
  }
  clear() {
    if (this.transaction) throw new Error('transaction is not ended');

    while (this.redos.length) freeUndo(this.redos.pop()!);
    while (this.undos.length) freeUndo(this.undos.pop()!);

    if (this.preundos.length) {
      freeUndo(this.preundos);
      this.preundos = [];
    }

    this.discardedUndos = false;
  }
  clearRedos() {
    while (this.redos.length) {
      freeUndo(this.redos.shift()!);
    }
  }
  unpre() {
    while (this.preundos.length) {
      this.preundos.pop()!()?.free?.();
    }
  }
  canUndo() {
    return this.undos.length > 0;
  }
  canRedo() {
    return this.redos.length > 0;
  }
  undo() {
    LOG && this.log('undo');
    this.undoOrRedo(this.undos, this.redos);
  }
  redo() {
    LOG && this.log('redo');
    this.undoOrRedo(this.redos, this.undos);
  }
  beginTransaction() {
    LOG && this.log('beginTransaction');

    if (this.transaction) throw new Error('transaction is not ended');

    this.preundos.forEach(p => p.pre = true);
    this.undos.push(this.preundos);
    this.transaction = true;
    this.preundos = [];
  }
  endTransaction() {
    if (!this.transaction) throw new Error('transaction is not started');

    this.transaction = false;
    LOG && this.log('endTransaction');
  }
  execTransaction(actions: (history: IHistory) => void) {
    this.beginTransaction();

    try {
      actions(this);
    } finally {
      this.endTransaction();
    }
  }
  pushUndo(undo: IUndoFunction) {
    if (this.preundos.length && !this.transaction) {
      LOG && this.log('(preundo transaction)');
      this.beginTransaction();
      this.pushUndo(undo);
      this.endTransaction();
      return;
    }

    if (this.transaction) {
      this.undos[this.undos.length - 1].push(undo);
    } else {
      this.undos.push([undo]);
    }

    this.clearRedos();

    while (this.undos.length > UNDO_LIMIT) {
      freeUndo(this.undos.shift()!);
      this.discardedUndos = true;
    }
  }
  prepushUndo(undo: IUndoFunction) {
    this.preundos.push(undo);
  }
  // DON'T USE: this causes desync on server
  cancelLastUndo() {
    LOG && this.log('cancelLastUndo');
    this.undo();
    this.clearRedos();
  }
  createSelection(type: string) {
    let snapshot = serializeMask(this.user.selection);

    log('  selection push');

    const swap: IUndoFunction = () => {
      log('  selection swap');
      LOG && this.log('createSelection:swap');
      const temp = serializeMask(this.user.selection);
      deserializeMask(this.user.selection, snapshot);
      snapshot = temp;
      redraw(this.editor);
      return swap;
    };

    swap.type = type;

    return swap;
  }
  pushSelection(type: string) {
    LOG && this.log('pushSelection');
    this.pushUndo(this.createSelection(type));
  }
  prepushSelection(type: string) {
    LOG && this.log('prepushSelection');
    this.prepushUndo(this.createSelection(type));
  }
  private createDirtyRect(type: string, layerId: number, dirtyRect: Rect, isMove?: boolean) {
    const editor = this.editor;

    // TODO: re-enable this check
    if (DEVELOPMENT && false && !isValidDirtyRect(dirtyRect, editor.drawing))
      throw new Error(`Invalid dirty rect (${rectToString(dirtyRect)}) / (${rectToString(editor.drawing.rect)})`);

    const rect = makeIntegerRect(rectsIntersection(dirtyRect, editor.drawing.rect)); // TODO: just clone rect instead
    const layer = getLayerSafe(editor.drawing, layerId);
    let snapshot = pushHistoryBufferEntry(this.undosBuffer, editor.renderer, layer, rect);
    let rectSnapshot = cloneRect(layer.rect);
    let isUndo = false;

    log('  rect push', 'layerId:', layerId, rect);

    const swap: IUndoFunction = () => {
      log('  rect swap', 'layerId:', layerId, rect);
      LOG && this.log('createDirtyRect:swap');
      const buffer = isUndo ? this.undosBuffer : this.redosBuffer;
      const layer = getLayerSafe(editor.drawing, layerId); // need to re-fetch layer here
      const temp = pushHistoryBufferEntry(buffer, editor.renderer, layer, rect);
      const tempRect = cloneRect(layer.rect);
      editor.renderer.restoreSnapshotToLayer(snapshot, layer, rectSnapshot);
      removeHistoryBufferEntry(snapshot, editor.renderer);
      redrawDrawing(editor, rect);
      redraw(editor);
      redrawLayerThumb(layer);
      snapshot = temp;
      rectSnapshot = tempRect;
      isUndo = !isUndo;
      return swap;
    };

    swap.type = type;
    swap.layerId = layerId;
    swap.isMove = isMove;
    swap.free = () => removeHistoryBufferEntry(snapshot, editor.renderer);
    return swap;
  }
  pushDirtyRect(type: string, layerId: number, dirtyRect: Rect, isMove?: boolean) {
    LOG && this.log('pushDirtyRect');
    this.pushUndo(this.createDirtyRect(type, layerId, dirtyRect, isMove));
  }
  prepushDirtyRect(type: string, layerId: number, dirtyRect: Rect, isMove?: boolean) {
    LOG && this.log('prepushDirtyRect');
    this.prepushUndo(this.createDirtyRect(type, layerId, dirtyRect, isMove));
  }
  private createTool(type: string, isMove?: boolean) {
    const editor = this.editor;
    const user = this.user;

    let isUndo = false;
    let surface = user.surface;
    let rect = cloneRect(surface.rect);
    let state = getSurfaceState(surface);
    let snapshot = pushHistoryBufferEntry(this.undosBuffer, editor.renderer, surface, rect);

    const id = stateId++;

    log('  state push', id, 'layerId:', state.layerId, state.rect);

    const swap: IUndoFunction = () => {
      log('  state swap', id, 'layerId:', state.layerId, state.rect);
      LOG && this.log('createTool:swap');
      const buffer = isUndo ? this.undosBuffer : this.redosBuffer;
      const tempState = getSurfaceState(surface);
      const tempRect = cloneRect(surface.rect);
      const temp = pushHistoryBufferEntry(buffer, editor.renderer, surface, tempRect);

      if (snapshot) {
        editor.renderer.restoreSnapshotToTool(snapshot, user);
      } else {
        editor.renderer.releaseUserCanvas(user);
      }

      setSurfaceState(surface, state, editor.drawing);

      removeHistoryBufferEntry(snapshot, editor.renderer);

      redrawDrawing(editor); // TODO: use rect ?
      redraw(editor);

      const layer1 = getLayer(editor.drawing, state.layerId);
      layer1 && layerChanged(layer1);

      const layer2 = getLayer(editor.drawing, tempState.layerId);
      layer2 && layerChanged(layer2);

      // TODO:
      //if (surface.empty())
      //  editor.renderer.releaseUserCanvas(user);

      snapshot = temp;
      rect = tempRect;
      state = tempState;
      isUndo = !isUndo;
      return swap;
    };

    swap.layerId = state.layerId;
    swap.isMove = isMove;
    swap.type = type;
    swap.free = () => removeHistoryBufferEntry(snapshot, editor.renderer);
    return swap;
  }
  private createLayerId(type: string, layerId: number) {
    const swap: IUndoFunction = () => swap;
    swap.layerId = layerId;
    swap.type = type;
    return swap;
  }
  pushTool(type: string, isMove?: boolean) {
    LOG && this.log('pushTool');
    this.pushUndo(this.createTool(type, isMove));
  }
  pushLayerId(type: string, layerId: number) {
    LOG && this.log('pushLayerId');
    this.pushUndo(this.createLayerId(type, layerId));
  }
  prepushTool(type: string, isMove?: boolean) {
    LOG && this.log('prepushTool');
    this.prepushUndo(this.createTool(type, isMove));
  }
  prepushLayerState(layerId: number) {
    LOG && this.log('prepushLayerState');
    this.prepushUndo(this.createLayerState(layerId));
  }
  pushLayerState(layerId: number) {
    LOG && this.log('pushLayerState');
    this.pushUndo(this.createLayerState(layerId));
  }
  createLayerState(layerId: number) {
    const layer = getLayerSafe(this.editor.drawing, layerId);
    let snapshot = toLayerState(layer);
    delete snapshot.rect;

    log('  layer push', 'layerId:', layerId);

    const swap: IUndoFunction = () => {
      log('  layer swap', 'layerId:', layerId);
      logAction('  swap layer state [history]');
      const layer = getLayerSafe(this.editor.drawing, layerId); // need to re-fetch layer here
      const wasTextLayer = isTextLayer(layer);
      const temp = toLayerState(layer);
      delete temp.rect;
      const selection = swap.textSelection;
      swap.textSelection = this.editor.textTool.getSelectionForUndo(layer);
      setLayerState(layer, snapshot);
      const rasterizationInvolved = wasTextLayer !== isTextLayer(layer);
      if (this.editor.textTool.isUsingTextTool) {
        if (layer === this.editor.textTool.layer || layer === this.editor.model.user.activeLayer) {
          if (rasterizationInvolved) this.editor.textTool.onLayerChange(layer);
          else this.editor.textTool.onLayerEnter(layer);
          this.editor.textTool.retrieveSelectionFromUndo(selection);
        }
      }
      snapshot = temp;
      redrawDrawing(this.editor);
      redraw(this.editor);
      return swap;
    };

    swap.layerId = layerId;
    swap.type = 'update layer';
    if (isTextLayer(layer)) {
      swap.isText = true;
      swap.textSelection = this.editor.textTool.getSelectionForUndo(layer);
    }

    return swap;
  }
  pushAddLayer(data: LayerData, index: number) {
    LOG && this.log('pushAddLayer');
    this.pushLayerAddRemove(data, index, true);
  }
  pushRemoveLayer(data: LayerData, index: number) {
    LOG && this.log('pushRemoveLayer');
    this.pushLayerAddRemove(data, index, false);
  }
  private pushLayerAddRemove(data: LayerData, index: number, adding: boolean) {
    let add: IUndoFunction, remove: IUndoFunction;

    log(`  layer ${adding ? 'add' : 'remove'} push`, 'layerId:', data.id);

    add = () => {
      log(`  layer ${adding ? 'add' : 'remove'} (add)`, 'layerId:', data.id);
      logAction(`  add layer (${data.id}) [history]`);
      const layer = layerFromState(data);
      if (layer.textData) {
        // we don't want to retrieve layer in non-dirty state so it gets deleted right away
        layer.textData.dirty = true;
      }
      addLayerToDrawing(this.editor, layer, index);
      if (this.editor.model.user === this.user) {
        sendLayerOrder(this.editor);
        selectLayer(this.editor, layer);
      }
      redrawDrawing(this.editor);
      return remove;
    };

    remove = () => {
      log(`  layer ${adding ? 'add' : 'remove'} (remove)`, 'layerId:', data.id);
      logAction(`  remove layer (${data.id}) [history]`);
      // TODO: save new index ???
      const layer = getLayer(this.editor.drawing, data.id);
      if (layer) this.editor.textTool.denyLayerAutoDelete(layer);
      removeLayerFromDrawing(this.editor, data.id);
      redrawDrawing(this.editor);
      redraw(this.editor);
      return add;
    };

    add.layerId = data.id;
    add.type = `${adding ? 'addLayer' : 'removeLayer'}`;
    remove.layerId = data.id;
    remove.type = `${adding ? 'addLayer' : 'removeLayer'}`;
    this.pushUndo(adding ? remove : add);
  }
  clearLayer(layerId: number) {
    if (this.transaction) throw new Error('transaction is not ended');

    const redosIndex = findLastIndex(this.redos, redo => redo.some(i => i.layerId === layerId));

    if (redosIndex !== -1) {
      this.redos.splice(0, redosIndex + 1).forEach(freeUndo);
      this.undos.forEach(freeUndo);
      this.undos = [];
    } else {
      const undosIndex = findLastIndex(this.undos, undo => undo.some(i => i.layerId === layerId));

      if (undosIndex !== -1) {
        const lastToRemove = this.undos[undosIndex];

        // check if only prepushes are connected to the layer
        if (lastToRemove.some(i => !i.pre && i.layerId === layerId)) {
          this.undos.splice(0, undosIndex + 1).forEach(freeUndo);
        } else {
          // only remove prepushed funcions from last one
          for (let i = lastToRemove.length - 1; i >= 0; i--) {
            if (lastToRemove[i].pre) {
              lastToRemove[i].free?.();
              lastToRemove.splice(i, 1);
            }
          }

          if (lastToRemove.length === 0) {
            this.undos.splice(0, undosIndex + 1).forEach(freeUndo);
          } else {
            this.undos.splice(0, undosIndex).forEach(freeUndo);
          }
        }
      }
    }

    if (this.preundos.some(x => x.layerId === layerId)) {
      freeUndo(this.preundos);
      this.preundos = [];
    }

    // TEMP: testing, change to DEVELOPMENT later
    if (this.redos.some(u => u.some(i => i.layerId === layerId))) {
      const msg = `Failed to clearLayer redos for layerId: ${layerId}, redos: [${this.redos?.map(undoLog)}]`;
      logAction(msg);
      if (DEVELOPMENT) throw new Error(msg);
    }
    if (this.undos.some(u => u.some(i => i.layerId === layerId))) {
      const msg = `Failed to clearLayer undos for layerId: ${layerId}, undos: ${undosLog(this)}`;
      logAction(msg);
      if (DEVELOPMENT) throw new Error(msg);
    }
  }
}

function freeUndo(undos: IUndoFunction[]) {
  for (const undo of undos) undo.free?.();
}

function undoLog(u: IUndoFunction[]) {
  const t = uniq(u.filter(x => x.type).map(x => `${x.type}${x.layerId ? `(${x.layerId})` : ''}`));
  return t.length ? t.join('+').replace(/\+move$/, '') : '?';
}

export function undosLog(history: IHistory) {
  return `[${(history as History).undos?.map(undoLog)}]`;
}

export function redosLog(history: IHistory) {
  return `[${(history as History).redos?.map(undoLog)}]`;
}
